/**
 * Related Assets — finds nearby infrastructure given an event's coordinates.
 *
 * Queries military bases, ports, undersea cables, pipelines, waterways,
 * and AI datacenters within a configurable radius (default 100 km).
 * Returns a sorted list of nearby assets with distance and bearing.
 */

import { haversineDistance, bearing, bearingToCompass } from '@/utils/geo';
import { MILITARY_BASES } from '@/config/military-bases';
import { PORTS } from '@/config/ports';
import { INFRA_NODES } from '@/config/infrastructure-graph';
import { AI_DATACENTERS } from '@/config/ai-datacenters';

/** Asset category for display grouping. */
export type AssetCategory =
  | 'military_base'
  | 'port'
  | 'cable'
  | 'pipeline'
  | 'waterway'
  | 'datacenter';

/** A single nearby infrastructure asset. */
export interface NearbyAsset {
  type: AssetCategory;
  name: string;
  distanceKm: number;
  bearingDeg: number;
  compass: string;
  /** Optional sub-type (e.g. 'navy', 'oil', 'gas'). */
  subtype?: string;
  /** Optional extra context (country, operator, capacity). */
  detail?: string;
}

const DEFAULT_RADIUS_KM = 100;

/** Category display labels. */
export const ASSET_CATEGORY_LABELS: Record<AssetCategory, string> = {
  military_base: 'Military Base',
  port: 'Port',
  cable: 'Undersea Cable',
  pipeline: 'Pipeline',
  waterway: 'Waterway',
  datacenter: 'AI Datacenter',
};

/**
 * Find all infrastructure assets within `radiusKm` of a given lat/lng.
 * Returns results sorted by distance ascending.
 */
export function findRelatedAssets(
  lat: number,
  lng: number,
  radiusKm: number = DEFAULT_RADIUS_KM,
): NearbyAsset[] {
  const results: NearbyAsset[] = [];

  // --- Military bases ---
  for (const base of MILITARY_BASES) {
    const dist = haversineDistance(lat, lng, base.lat, base.lng);
    if (dist <= radiusKm) {
      const brg = bearing(lat, lng, base.lat, base.lng);
      results.push({
        type: 'military_base',
        name: base.name,
        distanceKm: Math.round(dist * 10) / 10,
        bearingDeg: Math.round(brg),
        compass: bearingToCompass(brg),
        subtype: base.type,
        detail: `${base.operator} ${base.type} — ${base.country}`,
      });
    }
  }

  // --- Ports ---
  for (const port of PORTS) {
    const dist = haversineDistance(lat, lng, port.lat, port.lng);
    if (dist <= radiusKm) {
      const brg = bearing(lat, lng, port.lat, port.lng);
      results.push({
        type: 'port',
        name: port.name,
        distanceKm: Math.round(dist * 10) / 10,
        bearingDeg: Math.round(brg),
        compass: bearingToCompass(brg),
        subtype: port.type,
        detail: port.capacity
          ? `${port.type} — ${port.capacity}`
          : port.type,
      });
    }
  }

  // --- Infrastructure graph nodes (cables, pipelines, waterways) ---
  for (const node of INFRA_NODES) {
    // Skip countries (not physical infrastructure) and ports (already covered above)
    if (node.type === 'country') continue;
    // Map infrastructure-graph port nodes are duplicates of PORTS — skip
    if (node.type === 'port') continue;

    const dist = haversineDistance(lat, lng, node.lat, node.lng);
    if (dist <= radiusKm) {
      const brg = bearing(lat, lng, node.lat, node.lng);
      results.push({
        type: node.type as AssetCategory,
        name: node.name,
        distanceKm: Math.round(dist * 10) / 10,
        bearingDeg: Math.round(brg),
        compass: bearingToCompass(brg),
        detail: node.description,
      });
    }
  }

  // --- AI datacenters ---
  for (const dc of AI_DATACENTERS) {
    const dist = haversineDistance(lat, lng, dc.lat, dc.lng);
    if (dist <= radiusKm) {
      const brg = bearing(lat, lng, dc.lat, dc.lng);
      results.push({
        type: 'datacenter',
        name: dc.name,
        distanceKm: Math.round(dist * 10) / 10,
        bearingDeg: Math.round(brg),
        compass: bearingToCompass(brg),
        subtype: dc.operator,
        detail: dc.powerMW
          ? `${dc.operator} — ${dc.powerMW} MW`
          : dc.operator,
      });
    }
  }

  // Sort by distance ascending
  results.sort((a, b) => a.distanceKm - b.distanceKm);

  return results;
}
